/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import I18n from 'i18n-js';
import cx from 'classnames';
import {
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
  SCRIBE_SHOPIFY_STATES,
} from 'applications/scribe/instrumentations/track';
import GrabMaps, { Marker, RecenterControl, ZoomControl } from '@geo/grab-maps';
import { getMapTilesURL } from 'constants/app-constants';
import pickUpPinSvg from 'public/images/pick-up-2.svg';
import dropOffPinSvg from 'public/images/drop-off-2.svg';
import dropOffHighlightedPinSvg from 'public/images/drop-off-highlighted.svg';
import styles from './styles.less';
import ShopifyTrackEventContext from '../track-event';

const GrabMap = GrabMaps({
  dragRotate: false,
});

const getDisplayLongLatArr = (fulfillmentOrder, addressEntity) => {
  const latFromAddressEntity = get(addressEntity, ['latlng', 'latitude'], 0);
  const longFromAddressEntity = get(addressEntity, ['latlng', 'longitude'], 0);

  if (latFromAddressEntity && longFromAddressEntity) {
    return [longFromAddressEntity, latFromAddressEntity];
  }

  const latFromShopify =
    fulfillmentOrder &&
    get(fulfillmentOrder, ['shippingAddress', 'latitude'], 0);
  const longFromShopify =
    fulfillmentOrder &&
    get(fulfillmentOrder, ['shippingAddress', 'longitude'], 0);
  if (latFromShopify && longFromShopify) {
    return [longFromShopify, latFromShopify];
  }

  if (latFromAddressEntity && longFromAddressEntity) {
    return [longFromAddressEntity, latFromAddressEntity];
  }

  return null;
};

const MapView = ({
  shop,
  fulfillmentOrders,
  addressEntityMap,
  selectedFulfillmentOrders,
  onSelectFulfillmentOrder,
}) => {
  const { fireShopifyTrackEvent } = useContext(ShopifyTrackEventContext);
  const onPinClick = useCallback(
    (type, poiID, shopifyOrderID = undefined) => {
      fireShopifyTrackEvent({
        stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
        eventName:
          type === 'pickup'
            ? SCRIBE_SHOPIFY_EVENTS.PICKUP_POINT
            : SCRIBE_SHOPIFY_EVENTS.DROPOFF_POINT,
        facets:
          type === 'pickup'
            ? {
                [SCRIBE_SHOPIFY_PARAMS.PICKUP_POI_ID]: poiID,
              }
            : {
                [SCRIBE_SHOPIFY_PARAMS.DROPOFF_POI_ID]: poiID,
                [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: shopifyOrderID,
              },
      });
      if (type === 'dropoff') {
        const isOrderSelected = !!selectedFulfillmentOrders.find(
          o => o.orderID === shopifyOrderID,
        );
        const order = fulfillmentOrders.find(o => o.orderID === shopifyOrderID);
        if (!isOrderSelected) {
          onSelectFulfillmentOrder([
            ...selectedFulfillmentOrders.map(i => i.ID),
            get(order, 'ID'),
          ]);
        } else {
          onSelectFulfillmentOrder([
            ...selectedFulfillmentOrders
              .filter(o => o.orderID !== shopifyOrderID)
              .map(o => o.ID),
          ]);
        }
      }
    },
    [
      fireShopifyTrackEvent,
      selectedFulfillmentOrders,
      fulfillmentOrders,
      onSelectFulfillmentOrder,
    ],
  );

  const pickupLongLats = useMemo(
    () =>
      get(shop, 'pickUpAddresses', [])
        .filter(adr => adr.verified)
        .map(adr => [
          get(adr, 'grabPoi.coordinates.longitude'),
          get(adr, 'grabPoi.coordinates.latitude'),
        ]),
    [shop],
  );

  const pickUpMarkers = useMemo(
    () =>
      get(shop, 'pickUpAddresses', [])
        .filter(adr => adr.verified)
        .map(adr => (
          <Marker
            key={adr.poiID}
            onClick={() => onPinClick('pickup', adr.poiID)}
            coordinates={[
              get(adr, 'grabPoi.coordinates.longitude'),
              get(adr, 'grabPoi.coordinates.latitude'),
            ]}
            className={cx(
              'mapboxgl-marker',
              'mapboxgl-marker-anchor-bottom',
              styles.pickUpPinMarker,
            )}
            anchor="bottom"
          >
            <img className={styles.pickUpPin} alt="pickup" src={pickUpPinSvg} />
            <div className={styles.pickUpInfoOnPin}>
              <span className={styles.pickUpName}>
                {get(adr, 'shopifyLocation.name')}
              </span>
            </div>
          </Marker>
        )),
    [shop, onPinClick],
  );

  const selectedFulfillmentOrdersMap = useMemo(
    () =>
      selectedFulfillmentOrders.reduce(
        (map, fulfillmentOrder) => ({
          ...map,
          [fulfillmentOrder.ID]: true,
        }),
        {},
      ),
    [selectedFulfillmentOrders],
  );

  const longLatArrMap = useMemo(
    () =>
      fulfillmentOrders &&
      fulfillmentOrders.reduce((prev, fulfillmentOrder) => {
        const longLatArr = getDisplayLongLatArr(
          fulfillmentOrder,
          addressEntityMap[fulfillmentOrder.ID],
        );
        if (fulfillmentOrder.disabled || !longLatArr) {
          return prev;
        }
        return {
          ...prev,
          [fulfillmentOrder.ID]: longLatArr,
        };
      }, {}),
    [fulfillmentOrders, addressEntityMap],
  );

  const averageLongLat = useMemo(
    () => {
      if (!longLatArrMap) {
        return undefined;
      }
      const allLongLats = [...pickupLongLats, ...Object.values(longLatArrMap)];
      if (allLongLats.length === 0) {
        return undefined;
      }

      const avgLong =
        allLongLats.map(longLat => longLat[0]).reduce((s, a) => s + a, 0) /
        allLongLats.length;
      const avgLat =
        allLongLats.map(longLat => longLat[1]).reduce((s, a) => s + a, 0) /
        allLongLats.length;
      return [avgLong, avgLat];
    },
    [longLatArrMap, pickupLongLats],
  );

  const [hoveredDropoffMarker, setHoveredDropoffMarker] = useState({
    ID: false,
  });
  const dropOffMarkers = useMemo(
    () =>
      fulfillmentOrders
        .filter(fulfillmentOrder => !!longLatArrMap[fulfillmentOrder.ID])
        .map(fulfillmentOrder => {
          const selected = selectedFulfillmentOrdersMap[fulfillmentOrder.ID];
          return (
            <Marker
              key={fulfillmentOrder.ID}
              onClick={() =>
                onPinClick(
                  'dropoff',
                  get(addressEntityMap, [fulfillmentOrder.ID, 'poiID']),
                  fulfillmentOrder.orderID,
                )
              }
              coordinates={longLatArrMap[fulfillmentOrder.ID]}
              className={cx(
                'mapboxgl-marker',
                'mapboxgl-marker-anchor-bottom',
                {
                  [styles.selectedMarker]: selected,
                },
              )}
              anchor="bottom"
              onMouseEnter={() => {
                setHoveredDropoffMarker({
                  [fulfillmentOrder.ID]: true,
                });
              }}
              onMouseLeave={() => {
                setHoveredDropoffMarker({
                  [fulfillmentOrder.ID]: false,
                });
              }}
            >
              <img
                className={styles.dropOffPin}
                alt="dropoff"
                src={
                  selected || hoveredDropoffMarker[fulfillmentOrder.ID]
                    ? dropOffHighlightedPinSvg
                    : dropOffPinSvg
                }
              />
              {(selected || hoveredDropoffMarker[fulfillmentOrder.ID]) && (
                <div className={styles.infoOnPin}>
                  <span className={styles.customerName}>
                    {get(fulfillmentOrder, ['shippingAddress', 'name'])}
                  </span>
                  <span className={styles.orderName}>
                    {I18n.t('shopify.orders.order')}&nbsp;
                    {fulfillmentOrder.orderName}
                  </span>
                </div>
              )}
            </Marker>
          );
        }),
    [
      fulfillmentOrders,
      longLatArrMap,
      selectedFulfillmentOrdersMap,
      onPinClick,
      hoveredDropoffMarker,
    ],
  );

  const [zoomMag, setZoomMag] = useState(10);
  const zoom = useCallback(
    diff => {
      const newZoomMag = zoomMag + diff;
      setZoomMag(newZoomMag);
      fireShopifyTrackEvent({
        stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
        eventName:
          diff > 0
            ? SCRIBE_SHOPIFY_EVENTS.MAP_ZOOM_IN
            : SCRIBE_SHOPIFY_EVENTS.MAP_ZOOM_OUT,
        facets: {
          [SCRIBE_SHOPIFY_PARAMS.ZOOM_MAGNITUDE]: newZoomMag,
        },
      });
    },
    [zoomMag, setZoomMag, fireShopifyTrackEvent],
  );
  const onZoomControlClick = useCallback((_, diff) => zoom(diff), [zoom]);

  const onRecenterControlClick = useCallback(
    x => {
      fireShopifyTrackEvent({
        stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
        eventName: SCRIBE_SHOPIFY_EVENTS.MAP_CENTER,
      });
    },
    [fireShopifyTrackEvent],
  );

  return (
    <div className={styles.mapView}>
      <GrabMap
        mapStyle={getMapTilesURL()}
        center={averageLongLat}
        zoom={zoomMag}
        fitBoundsOptions={{
          offset: [30, 30],
        }}
      >
        {pickUpMarkers}
        {dropOffMarkers}
        <ZoomControl
          className={styles.zoomControls}
          onClick={onZoomControlClick}
        />
        <RecenterControl
          className={styles.recenterControl}
          onClick={onRecenterControlClick}
        />
      </GrabMap>
    </div>
  );
};

MapView.propTypes = {
  shop: PropTypes.shape({}).isRequired,
  fulfillmentOrders: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  addressEntityMap: PropTypes.shape({}).isRequired,
  selectedFulfillmentOrders: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onSelectFulfillmentOrder: PropTypes.func.isRequired,
};

export default MapView;
